import { get, post, put } from "aws-amplify/api";
import { default as React, useState, useEffect, useCallback, useMemo } from "react";
import { GenericTable } from "components/GenericTable";
import EditableCell from "containers/common/EditableCell";
import { PacmanLoader } from "react-spinners";
import { Alert, Button, Col, Row } from "react-bootstrap";
import "react-datepicker/dist/react-datepicker.css";
import "containers/common/List.css";
import { createElementIdFactory, createReusableTableElementIds } from "utils/elementIds";
import { PAGE_SIZES } from "utils/pageSizeConfig";

import {
  createColumnHelper,
} from "@tanstack/react-table";

const columnHelper = createColumnHelper();

function SalutationList({ isAuthenticated, user }) {
  const salutationIdPrefix = "salutation-list";

  const [isLoading, setIsLoading] = useState(true);
  const [ui, setUi] = useState({ showAlert: false, errorMessage: "" });
  const [sorting, setSorting] = useState([]);
  const [data, setData] = useState([]);

  // --- API Functions ---
  const getData = useCallback(async () => {
    const response = await get({
      apiName: "colin",
      path: "/salutations/all",
    }).response;
    try {
      const pos = await response.body.json();
      console.log("Salutations Data:", pos);
      return pos;
    } catch (error) {
      alert(error + ". Please refresh the page (F5) to try again.");
    }
  }, []);

  useEffect(() => {
    setIsLoading(true);
    if (!isAuthenticated) {
      return;
    }

    (async () => {
      try {
        const localsalutations = await getData();
        setData(localsalutations || []);
      } catch (e) {
        alert(e);
      }
      setIsLoading(false);
    })();
  }, [isAuthenticated, getData]);

  const saveRecord = async (record) => {
    const isMissingId = record.id === undefined || record.id === null || record.id === "";
    const isNew = !!record.new_record || isMissingId;
    const path = isNew ? "/salutations/new" : `/salutations/${record.id}`;
    const method = isNew ? post : put;

    try {
      const result = await method({
        apiName: "colin",
        path: path,
        options: {
          body: {
            ...record,
            id: record.id ?? null,
            salutation: record.salutation,
            last_edited_by: user,
          }
        }
      });
      const response = await result.response;
      return await response.body.json();
    } catch (err) {
      console.error("Save error:", err);
      throw err;
    }
  };

  // --- Table Logic ---
  const updateTableState = (rowIndex, columnId, value) => {
    setData(old => old.map((record, index) => (
      index === rowIndex
        ? { ...record, [columnId]: value, modified: true }
        : record
    )));
  };

  // --- Save Logic Functions ---
  const handleSave = async ({ row, column, value }) => {
    const recordToSave = { ...row.original, [column.id]: value };

    if (recordToSave.new_record && !recordToSave.salutation) return;
    if (!recordToSave.new_record && (recordToSave.id === undefined || recordToSave.id === null || recordToSave.id === "")) {
      setUi({ showAlert: true, errorMessage: "Missing salutation id. Please refresh and try again." });
      return false;
    }
    if (recordToSave.isSaving) return false;

    setData(old => old.map((r, index) =>
      index === row.index ? { ...r, isSaving: true, [column.id]: value } : r
    ));

    try {
      const response = await saveRecord(recordToSave);

      if (response?.result === false) {
        setData(old => old.map((r, index) =>
          index === row.index ? { ...r, isSaving: false } : r
        ));
        setUi({ showAlert: true, errorMessage: response.error_message || "Failed to save the record." });
        return false;
      }

      const persistedId = response?.id ?? recordToSave.id;
      const hasPersistedId = persistedId !== undefined && persistedId !== null && persistedId !== "";

      setData(old => old.map((r, index) =>
        index === row.index
          ? {
            ...r,
            [column.id]: value,
            id: hasPersistedId ? persistedId : r.id,
            modified: false,
            isSaving: false,
            new_record: hasPersistedId ? false : !!r.new_record,
          }
          : r
      ));
      setUi({ showAlert: false, errorMessage: "" });
      return true;
    } catch (err) {
      setData(old => old.map((r, index) =>
        index === row.index ? { ...r, isSaving: false } : r
      ));
      setUi({ showAlert: true, errorMessage: "Network Error: Could not save record." });
      return false;
    }
  };

  const addRecord = () => {
    setData(prev => [{
      salutation: "",
      created_by: user,
      last_edited_by: user,
      new_record: true,
    }, ...prev]);
  };

  // --- Column Definitions ---
  const columns = useMemo(() => [
    columnHelper.accessor("salutation", {
      id: "salutation",
      header: "Salutation",
      cell: EditableCell,
      size: 250,
    }),
  ], []);

  const createId = useMemo(
    () => createElementIdFactory(salutationIdPrefix),
    [salutationIdPrefix]
  );

  const tableElementIds = useMemo(
    () => createReusableTableElementIds(createId),
    [createId]
  );

  if (!isAuthenticated) return null;

  // --- Render Functions ---
  return (
    <div className="mt-3">
      <Alert show={ui.showAlert} variant="danger" onClose={() => setUi({ ...ui, showAlert: false })} dismissible>
        {ui.errorMessage}
      </Alert>

      <Col className="text-right" style={{ marginBottom: '12px' }}>
        <Button id={createId("add", "button")} variant="secondary" size="sm" onClick={addRecord}>Add</Button>
      </Col>

      <Row>
        <Col>
          {isLoading ? (
            <div className="outer-div">
              <div className="sweet-loading inner-div">
                <PacmanLoader
                  cssOverride={{ display: "block", margin: "0 auto" }}
                  size={20}
                  color={"#e03c31"}
                  loading={isLoading}
                />
              </div>
            </div>
          ) : (
            <div className="custom-data-table">
              <GenericTable
                columns={columns}
                data={data}
                section="salutation-list"
                elementIds={tableElementIds}
                pageSize={PAGE_SIZES.salutation}
                enablePagination={true}
                state={{ sorting }}
                onSortingChange={setSorting}
                enableSortingRemoval={false}
                meta={{
                  updateData: updateTableState,
                  handleSave: handleSave,
                }}
              />
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
}

export default SalutationList;